import { useSiteSetupActions } from "@inrange/building-manager-api-client";
import {
  getESysName,
  Site,
  SiteElectricalSystem,
} from "@inrange/building-manager-api-client/models-site";
import { Icons } from "@inrange/theme-components/icons";
import { useState } from "react";
import { Alert, Badge, Button, Col, Form, Row, Spinner } from "react-bootstrap";

const SoracomSimSection = ({
  site,
  esysEntries,
  ingestionByEsys,
  simStatusByEsys,
  registerSoracomSim,
  importSoracomSim,
}: {
  site: Site;
  esysEntries: [string, SiteElectricalSystem][];
  ingestionByEsys: ReturnType<typeof useSiteSetupActions>["ingestionByEsys"];
  simStatusByEsys: ReturnType<typeof useSiteSetupActions>["simStatusByEsys"];
  registerSoracomSim: ReturnType<
    typeof useSiteSetupActions
  >["registerSoracomSim"];
  importSoracomSim: ReturnType<typeof useSiteSetupActions>["importSoracomSim"];
}) => {
  const [simAction, setSimAction] = useState<
    | { type: "register"; esysUuid: string }
    | { type: "import"; esysUuid: string }
    | null
  >(null);
  const [simImsi, setSimImsi] = useState("");
  const [simRegistrationSecret, setSimRegistrationSecret] = useState("");
  const [simResult, setSimResult] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  return (
    <div>
      <h6 className="fw-bold mb-2">Soracom SIM</h6>
      <div className="d-flex flex-column gap-2">
        {esysEntries.map(([key, esys]) => {
          const typedEsys = esys as SiteElectricalSystem;
          const ingestionQuery = ingestionByEsys[typedEsys.uuid];
          const esysConfig = ingestionQuery?.data ?? null;
          const soracomImsi = esysConfig?.site?.soracom_imsi;
          const simStatusQuery = simStatusByEsys[typedEsys.uuid];
          const simStatus = simStatusQuery?.data?.sim_status;
          const esysLabel =
            esysEntries.length > 1
              ? `Energy match${getESysName(esysEntries.length, typedEsys)}`
              : null;

          return (
            <div key={key}>
              <div className="d-flex align-items-center gap-2">
                {esysLabel && <span className="fw-semibold">{esysLabel}:</span>}
                {ingestionQuery?.isLoading ? (
                  <Spinner animation="border" size="sm" />
                ) : soracomImsi ? (
                  <>
                    <Badge bg="success">IMSI: {soracomImsi}</Badge>
                    {simStatusQuery?.isLoading ? (
                      <Spinner animation="border" size="sm" />
                    ) : simStatus ? (
                      <Badge bg={simStatus.online ? "success" : "danger"}>
                        {simStatus.online ? "Online" : "Offline"}
                      </Badge>
                    ) : null}
                    {simStatus?.imei && (
                      <span className="text-muted small">
                        IMEI: {simStatus.imei}
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <Badge bg="secondary">No SIM</Badge>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => {
                        setSimAction({
                          type: "register",
                          esysUuid: typedEsys.uuid,
                        });
                        setSimImsi("");
                        setSimRegistrationSecret("");
                        setSimResult(null);
                      }}
                    >
                      Register new SIM
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        setSimAction({
                          type: "import",
                          esysUuid: typedEsys.uuid,
                        });
                        setSimImsi("");
                        setSimRegistrationSecret("");
                        setSimResult(null);
                      }}
                    >
                      Import existing SIM
                    </Button>
                  </>
                )}
              </div>

              {simAction && simAction.esysUuid === typedEsys.uuid && (
                <div className="border rounded p-2 mt-2">
                  <div className="fw-semibold small mb-2 d-flex align-items-center gap-2">
                    {simAction.type === "register"
                      ? "Register new SIM"
                      : "Import existing SIM"}
                    <a
                      href="https://console.soracom.io/sims"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "#0d6efd",
                        textDecoration: "none",
                        fontWeight: "normal",
                      }}
                    >
                      Soracom portal
                      <img
                        alt="view"
                        src={Icons.shareBlue}
                        style={{
                          width: "14px",
                          height: "14px",
                          marginLeft: "4px",
                          marginBottom: "2px",
                        }}
                      />
                    </a>
                  </div>
                  <Row className="align-items-end g-2">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="small mb-1">IMSI</Form.Label>
                        <Form.Control
                          type="text"
                          size="sm"
                          value={simImsi}
                          onChange={(e) => setSimImsi(e.target.value)}
                          placeholder="e.g. 295050012345678"
                        />
                      </Form.Group>
                    </Col>
                    {simAction.type === "register" && (
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label className="small mb-1">
                            Registration secret
                          </Form.Label>
                          <Form.Control
                            type="text"
                            size="sm"
                            value={simRegistrationSecret}
                            onChange={(e) =>
                              setSimRegistrationSecret(e.target.value)
                            }
                          />
                        </Form.Group>
                      </Col>
                    )}
                    <Col xs="auto" className="d-flex gap-2">
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={
                          !simImsi ||
                          (simAction.type === "register" &&
                            !simRegistrationSecret) ||
                          registerSoracomSim.isLoading ||
                          importSoracomSim.isLoading
                        }
                        onClick={async () => {
                          setSimResult(null);
                          try {
                            if (simAction.type === "register") {
                              await registerSoracomSim.mutateAsync({
                                siteId: site.id,
                                esysUuid: simAction.esysUuid,
                                imsi: simImsi,
                                registration_secret: simRegistrationSecret,
                              });
                              await importSoracomSim.mutateAsync({
                                siteId: site.id,
                                esysUuid: simAction.esysUuid,
                                imsi: simImsi,
                              });
                              setSimResult({
                                type: "success",
                                message: `SIM ${simImsi} registered and associated with site.`,
                              });
                            } else {
                              await importSoracomSim.mutateAsync({
                                siteId: site.id,
                                esysUuid: simAction.esysUuid,
                                imsi: simImsi,
                              });
                              setSimResult({
                                type: "success",
                                message: `SIM ${simImsi} imported and associated with site.`,
                              });
                            }
                            setSimAction(null);
                          } catch (err) {
                            const errorMessage =
                              err instanceof Error
                                ? err.message
                                : "Operation failed";
                            setSimResult({
                              type: "error",
                              message: errorMessage,
                            });
                          }
                        }}
                      >
                        {registerSoracomSim.isLoading ||
                        importSoracomSim.isLoading ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            Processing...
                          </>
                        ) : simAction.type === "register" ? (
                          "Register & associate"
                        ) : (
                          "Import & associate"
                        )}
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setSimAction(null)}
                      >
                        Cancel
                      </Button>
                    </Col>
                  </Row>
                  {simResult && (
                    <Alert
                      variant={
                        simResult.type === "success" ? "success" : "danger"
                      }
                      dismissible
                      onClose={() => setSimResult(null)}
                      className="mt-2 mb-0"
                    >
                      {simResult.message}
                    </Alert>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {esysEntries.some(([, esys]) => {
        const ingestionQuery =
          ingestionByEsys[(esys as SiteElectricalSystem).uuid];
        return !!ingestionQuery?.data?.site?.soracom_imsi;
      }) && (
        <a
          className="d-inline-block mt-2"
          href="https://console.soracom.io/sims"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "#0d6efd",
            textDecoration: "none",
          }}
        >
          Soracom monitoring portal
          <img
            alt="view"
            src={Icons.shareBlue}
            style={{
              width: "17px",
              height: "17px",
              marginLeft: "8px",
              marginBottom: "2px",
            }}
          />
        </a>
      )}
    </div>
  );
};

export default SoracomSimSection;
